/**
 * Clean-up after sentences and fragments have been dropped. PLAN.md 3.6 rule 4.
 *
 * Works on a flat character stream so rules apply across run boundaries (a
 * bold run followed by a plain run). Every rule only DELETES characters (or
 * maps whitespace to a space 1:1), so marks and holes are never corrupted.
 */
import { sameMarks } from '@saledeed/shared';
import type { OutRun } from './output';

/** Stands in for a hole: not a space, not punctuation, so no rule touches it. */
const HOLE = '\uE000';
const CLOSERS = new Set(['.', ',', ';', ':', ')', ']', '!', '?']);
const OPENERS = new Set(['(', '[']);

interface Cell { ch: string; seg: number }

/**
 * One left-to-right pass. `drop` sees the previously KEPT character and the
 * remaining input, so decisions compose correctly within a single pass.
 */
function pass(cells: readonly Cell[], drop: (ch: string, prev: string | undefined, next: (k?: number) => string | undefined) => boolean): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i < cells.length; i++) {
    const next = (k = 1) => cells[i + k]?.ch;
    if (!drop(cells[i]!.ch, out[out.length - 1]?.ch, next)) out.push(cells[i]!);
  }
  return out;
}

/** Next character after any spaces. */
function nextNonSpace(next: (k?: number) => string | undefined): string | undefined {
  let k = 1;
  while (next(k) === ' ') k++;
  return next(k);
}

function removeEmptyBrackets(cells: readonly Cell[]): Cell[] {
  const out: Cell[] = [];
  for (const cell of cells) {
    const prev = out[out.length - 1];
    if (prev && ((prev.ch === '(' && cell.ch === ')') || (prev.ch === '[' && cell.ch === ']'))) {
      out.pop(); // drop the pair; handles nesting since we compare against kept output
      continue;
    }
    out.push(cell);
  }
  return out;
}

export function normalizeRuns(runs: readonly OutRun[]): OutRun[] {
  let cells: Cell[] = [];
  runs.forEach((r, seg) => {
    if (r.kind === 'hole') cells.push({ ch: HOLE, seg });
    else for (const ch of r.text) cells.push({ ch: /\s/.test(ch) ? ' ' : ch, seg });
  });

  // Rules can enable each other (removing "()" leaves "  ,"), so iterate to a fixed point.
  for (let iter = 0; iter < 8; iter++) {
    const before = cells.length;

    // collapse repeated spaces; drop leading spaces
    cells = pass(cells, (ch, prev) => ch === ' ' && (prev === undefined || prev === ' '));
    // no space before closing punctuation
    cells = pass(cells, (ch, _prev, next) => ch === ' ' && CLOSERS.has(next() ?? ''));
    // no space just inside an opening bracket
    cells = pass(cells, (ch, prev) => ch === ' ' && prev !== undefined && OPENERS.has(prev));
    // empty brackets left by a dropped fragment
    cells = removeEmptyBrackets(cells);
    // doubled separators: ", ," -> ","  ", ." -> "."  "; ." -> "."
    cells = pass(cells, (ch, _prev, next) => {
      if (ch !== ',' && ch !== ';') return false;
      const n = nextNonSpace(next);
      return n === ',' || n === '.' || n === ';';
    });
    // no separator at the very start
    cells = pass(cells, (ch, prev) => prev === undefined && (ch === ',' || ch === ';' || ch === ':'));

    if (cells.length === before) break;
  }

  while (cells.length && cells[cells.length - 1]!.ch === ' ') cells.pop();

  // Rebuild runs, merging neighbours with identical marks.
  const out: OutRun[] = [];
  for (const cell of cells) {
    const src = runs[cell.seg]!;
    if (src.kind === 'hole') {
      out.push(src);
      continue;
    }
    const last = out[out.length - 1];
    if (last && last.kind === 'text' && sameMarks(last.marks, src.marks)) last.text += cell.ch;
    else out.push({ kind: 'text', text: cell.ch, marks: src.marks });
  }
  return out;
}

export function isBlank(runs: readonly OutRun[]): boolean {
  return runs.every(r => r.kind === 'text' && r.text.trim() === '');
}
